import React, { useState, useRef, useEffect } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import { eventGroupsOnWeb } from '@lib/feature-flags'

const EventStrip = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const scrollContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Fetch events data with SWR options for better caching
  const { data: eventsData, error, isLoading } = useSWR(
    eventGroupsOnWeb() ? '/events/upcoming?group=1' : '/events/upcoming',
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000, // 5 minutes
      focusThrottleInterval: 300000, // 5 minutes
      shouldRetryOnError: false
    }
  )

  // Get unique categories from events
  const categories = eventsData?.categories || []
  const categoryColors = eventsData?.category_colors || {}
  const events = eventsData?.events || []

  // Filter events by selected category
  const filteredEvents = selectedCategory === 'all' 
    ? events 
    : events.filter(event => 
        event.category_list?.some(cat => 
          cat.toLowerCase() === selectedCategory.toLowerCase()
        )
      )

  // Check scroll position
  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
  }

  useEffect(() => {
    checkScrollPosition()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollPosition)
      // Check on resize
      window.addEventListener('resize', checkScrollPosition)
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition)
        window.removeEventListener('resize', checkScrollPosition)
      }
    }
  }, [filteredEvents])

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }

  // Get category color from backend data
  const getCategoryColor = (category) => {
    return categoryColors[category] || categoryColors[category.toLowerCase()] || 'bg-gray-500'
  }

  // Format category name for display (always lowercase)
  const formatCategoryName = (category) => {
    return category.toLowerCase()
  }

  // Show loading placeholder to maintain layout
  if (isLoading) {
    return (
      <div className="w-full bg-white dark:bg-gray-800 py-6 rounded-lg animate-pulse">
        <div className="px-4 sm:px-6">
          {/* Category skeleton */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2 justify-center lg:justify-between items-center">
              <div className="flex flex-wrap gap-2 justify-center">
                {/* Skeleton category buttons */}
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                ))}
              </div>
              <div className="hidden lg:block h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
          
          {/* Event strip skeleton */}
          <div className="relative">
            <div className="flex gap-4 overflow-hidden">
              {/* Skeleton event cards */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex-shrink-0">
                  <div className="relative overflow-hidden rounded-lg">
                    <div className="relative aspect-[4/3] w-64 bg-gray-200 dark:bg-gray-700">
                      {/* Title overlay skeleton */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <div className="h-4 w-3/4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                        <div className="h-3 w-1/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                      </div>
                      {/* Category badge skeleton */}
                      <div className="absolute top-2 left-2 h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  if (error || !eventsData) return null // Hide on error

  return (
    <div className="w-full bg-white dark:bg-gray-800 py-6 rounded-lg">
      <div className="px-4 sm:px-6">
        {/* Category Navigation */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2 justify-center lg:justify-between items-center">
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer bg-gray-600 text-white ${
                  selectedCategory === 'all'
                    ? 'ring-2 ring-offset-2 ring-gray-600 dark:ring-offset-gray-800'
                    : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-600 dark:hover:ring-offset-gray-800'
                }`}
              >
                All
              </button>
              {categories.map((category) => {
                const categoryColor = getCategoryColor(category)
                const isSelected = selectedCategory === category.toLowerCase()
                
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category.toLowerCase())}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer text-white
                      ${categoryColor}
                      ${isSelected 
                        ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800' 
                        : 'hover:ring-2 hover:ring-offset-2 hover:ring-offset-white dark:hover:ring-offset-gray-800'
                      }
                    `}
                  >
                    {formatCategoryName(category)}
                  </button>
                )
              })}
            </div>
            {/* Show all link - hidden on mobile, pushed right on desktop */}
            <a
              href={`/whatson${selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''}`}
              className="hidden lg:flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              Show all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
          {/* Show all link for mobile - centered below categories */}
          <div className="flex justify-center mt-3 lg:hidden">
            <a
              href={`/whatson${selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''}`}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              Show all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Event Image Strip */}
        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              aria-label="Scroll left"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              aria-label="Scroll right"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {filteredEvents.map((event) => (
              <a
                key={`${event.type || 'event'}-${event.id}`}
                href={event.url || `/events/${event.slug}`}
                className="flex-shrink-0 group"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="relative overflow-hidden rounded-lg">
                  {/* Event Image */}
                  <div className="relative aspect-[4/3] w-64">
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-gray-500 dark:text-gray-400">No image</span>
                      </div>
                    )}
                    
                    {/* Event Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <h3 className="text-white font-semibold text-sm line-clamp-2">
                        {event.name}
                      </h3>
                      {event.date && (
                        <p className="text-white/80 text-xs mt-1">
                          {event.type === 'event_group' && 'Next '}
                          {new Date(event.date).toLocaleDateString('en-GB', { 
                            day: 'numeric',
                            month: 'short'
                          })}
                          {event.type === 'event_group' && ` · ${event.upcoming_count} ${event.upcoming_count === 1 ? 'session' : 'sessions'}`}
                        </p>
                      )}
                    </div>

                    {/* Category Badge */}
                    {event.main_category && (
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded text-white text-xs font-medium ${event.main_category_color || getCategoryColor(event.main_category)}`}>
                        {formatCategoryName(event.main_category)}
                      </div>
                    )}

                    {/* Status Badges */}
                    {event.free_of_charge ? (
                      <div className="absolute top-2 right-2">
                        <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                          FREE
                        </span>
                      </div>
                    ) : event.sold_out && !event.needs_phone_booking ? (
                      <div className="absolute top-2 right-2">
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                          SOLD OUT
                        </span>
                      </div>
                    ) : event.limited_availability ? (
                      <div className="absolute top-2 right-2">
                        <span className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded">
                          LIMITED
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventStrip